import React, { useLayoutEffect, useImperativeHandle, useEffect, useCallback, useState, RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useI18n } from 'i18n';

import { asyncRequest } from 'helpers/asyncRequest';

import { eventEmitter, wsService } from '../../../index';
import { EventEmitterMessages } from 'constants/event-emitter';

import { zonesMapSelector } from 'core/redux/selectors/zones';
import { widgetsSelector } from 'core/redux/selectors/widgets';
import { zoneGuardMapSelector } from 'core/redux/selectors/zoneGuards';

import { actions as zonesActions } from 'core/redux/api/zones';
import { actions as widgetsActions } from 'core/redux/app/widgets';
import { actions as zoneGuardsActions } from 'core/redux/api/zoneGuards';
import { actions as occupancyActions } from 'core/redux/api/zoneGuardsOccupancy';

import { WidgetConfiguration } from 'core/react/custom/widgets/live-zone-occupancy/widget-configuration';

interface IWidgetProps {
  uid: string;
  reduxKey: string;
  expanded: boolean;
  customRef: RefObject<any>;
  update(): void;
  sizeChange(layout: any): void;
}

export const useLiveZoneOccupancy = (props: IWidgetProps) => {
  const { uid, reduxKey, customRef, expanded, update, sizeChange } = props;

  const { fc } = useI18n();

  const dispatch = useDispatch();

  const widgets = useSelector(widgetsSelector);
  const zones = useSelector(zonesMapSelector);
  const zoneGuards = useSelector(zoneGuardMapSelector);

  const [isLoading, setLoading] = useState<boolean>(false);

  const widget = widgets[reduxKey].map[uid];
  const processorId = widget.data?.processorId;

  const widgetData = widget && widget.data;

  const updateHeight = () => {
    const layout = { ...widget.layout };

    sizeChange({ ...layout, h: 35 });
  };

  const updateTitle = (title: string) => {
    dispatch(widgetsActions.update({ domain: reduxKey, uid, data: { title } }));
  };

  const toggleSortByZone = (value: boolean) => {
    dispatch(widgetsActions.update({ domain: reduxKey, uid, data: { isSortedByZone: value } }));
  };

  const fetchZoneGuardOccupancy = async (processorId: number, isSocket?: boolean) => {
    try {
      if (!isSocket) {
        setLoading(true);
      }

      const data = await asyncRequest(`processors/zone-guards/${processorId}/occupancy`);

      dispatch(occupancyActions.addZoneGuardOccupancy({ id: processorId, occupancy: data }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZoneGuardMustering = async (processorId: number, isSocket?: boolean) => {
    try {
      if (!isSocket) {
        setLoading(true);
      }

      const data = await asyncRequest(`processors/zone-guards/${processorId}/mustering`);

      dispatch(occupancyActions.addZoneGuardMustering({ id: processorId, mustering: data }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectZoneGuard = async (processorId: number) => {
    updateHeight();

    dispatch(
      widgetsActions.update({ domain: reduxKey, uid, data: { processorId, title: zoneGuards[processorId].name } }),
    );

    await fetchZoneGuardOccupancy(processorId);
  };

  const onZoneOccupancyUpdate = useCallback(
    async (processorId: number) => {
      if (widgetData?.isEmergencyMode) {
        await fetchZoneGuardMustering(processorId, true);
      } else {
        await fetchZoneGuardOccupancy(processorId, true);
      }
    },
    [widgetData?.isEmergencyMode],
  );

  useEffect(() => {
    if (!zones) {
      dispatch(zonesActions.load.request());
    }

    if (!zoneGuards) {
      dispatch(zoneGuardsActions.load.request());
    }
  }, []);

  useEffect(() => {
    if (!processorId) {
      return;
    }

    wsService.subscribeToZoneGuardOccupancy(processorId);

    return () => {
      wsService.unsubscribeZoneGuardOccupancy(processorId);
    };
  }, [processorId]);

  useEffect(() => {
    eventEmitter.on(EventEmitterMessages.ZoneOccupancyUpdate, onZoneOccupancyUpdate);

    return () => {
      eventEmitter.removeListener(EventEmitterMessages.ZoneOccupancyUpdate, onZoneOccupancyUpdate);
    };
  }, [onZoneOccupancyUpdate]);

  useEffect(() => {
    if (processorId) {
      fetchZoneGuardOccupancy(processorId);
    }
  }, []);

  useEffect(() => {
    if (processorId) {
      if (widgetData?.isEmergencyMode) {
        updateTitle('Emergency Mode');
        fetchZoneGuardMustering(processorId);
      } else {
        if (widgetData?.title === 'Emergency Mode') {
          updateTitle(zoneGuards?.[processorId].name);
        }

        fetchZoneGuardOccupancy(processorId);
      }
    }
  }, [widgetData?.isEmergencyMode]);

  useLayoutEffect(() => {
    update();
  }, [widgetData]);

  useImperativeHandle(customRef, () => ({
    title: () => fc('Live Zone Occupancy'),
    configuration: () => <WidgetConfiguration reduxKey={reduxKey} uid={uid} />,
    scrollable: true,
  }));

  const occupancyProps = {
    isLoading,
    isEmergencyMode: widgetData?.isEmergencyMode,
    isSortedByZone: widgetData?.isSortedByZone,
    toggleSortByZone,
  };

  return { processorId, occupancyProps, selectZoneGuard };
};
